/**
 * HTTP client (skeleton).
 *
 * Security posture carried from DATAROOM_AUDIT:
 *   • The magic-link session JWT is read ONCE from the URL then held in memory
 *     and sent as an Authorization header — never left in the address bar,
 *     Referer, or access logs (fixes S2 "token in URL query string").
 *   • Client-supplied geo/IP are NOT trusted headers; regulated geo/consent are
 *     captured and bound server-side (fixes S3). This client never spoofs them.
 *
 * MOCK mode is an EXPLICIT, fail-CLOSED opt-in (VITE_MOCK=true) for local dev with
 * no backend — never the default. Previously an empty VITE_API_BASE_URL silently
 * enabled mock (fail-OPEN), which shipped mock fixtures to the deployed build on
 * 2026-07-11. Now, absent the explicit opt-in, the app ALWAYS targets the real
 * backend and fails honestly if none is reachable — a misconfigured base URL can
 * never masquerade as a working (fabricated) app (de-demo P1-D).
 */

export const API_BASE_URL: string = import.meta.env.VITE_API_BASE_URL ?? '';
export const isMockMode = (): boolean => import.meta.env.VITE_MOCK === 'true';

let sessionToken: string | null = null;
let tokenExpiresAtMs = 0;
let exchangeInFlight: Promise<string | null> | null = null;

/** Store the bearer in memory (see `exchangeSessionToken` for where it comes from). */
export function setSessionToken(token: string | null): void {
  sessionToken = token;
  tokenExpiresAtMs = 0;
}

/**
 * Exchange the session id from the invite link for a SIGNED, EXPIRING, session-scoped bearer.
 *
 * **What changed and why.** The bearer used to BE the session id — unsigned, never expiring,
 * unrevocable, and the same string that appears in URLs and logs. Now the id buys one token,
 * once, at the front door; everything after is properly authenticated, dies on its own, and stops
 * working the moment the session ends (the server re-reads session state on every verification, so
 * ending a session revokes its token with no separate mechanism to remember).
 *
 * **What did NOT change, stated plainly:** the emailed link is still the bootstrap credential —
 * anyone holding it can make this exchange. Closing that needs a front-door challenge, which is a
 * product decision rather than an auth-layer one.
 *
 * **There is deliberately NO raw-id fallback.** An earlier version sent the raw session id when
 * this exchange failed, "so an older backend keeps working" — but against every deployed backend
 * the raw id is no longer a credential, so that fallback produced a customer who silently failed
 * every subsequent call instead of degrading. A refusal here now returns null and the caller
 * routes to the state-aware landing, which tells the customer honestly what state their session
 * is in.
 */
export async function exchangeSessionToken(sessionId: string): Promise<string | null> {
  if (isMockMode()) {
    setSessionToken(sessionId);
    return sessionId;
  }
  // Concurrent callers (the resume path can fire twice) share ONE exchange rather than racing two.
  if (exchangeInFlight) return exchangeInFlight;
  exchangeInFlight = (async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/customer-token`,
        { method: 'POST', headers: { Accept: 'application/json' } },
      );
      if (!res.ok) {
        if (res.status >= 500) {
          // A server fault is not a verdict on the session. Keep whatever bearer we already hold
          // (a near-death token in hand still works until its real expiry); the caller's own loads
          // surface the outage.
          return sessionToken;
        }
        // 4xx = the server ANSWERED and refused: unknown, finished, or dead session. The raw id
        // is not a credential against any current backend, so there is nothing to degrade to —
        // report the refusal and let the caller route to the honest state-aware landing.
        sessionToken = null;
        tokenExpiresAtMs = 0;
        return null;
      }
      const body = (await res.json()) as { token: string; expiresAt: string };
      sessionToken = body.token;
      tokenExpiresAtMs = Date.parse(body.expiresAt) || 0;
      return sessionToken;
    } catch {
      // Network fault — same as a server fault: keep what we hold, never mint a dead credential.
      return sessionToken;
    } finally {
      exchangeInFlight = null;
    }
  })();
  return exchangeInFlight;
}

/**
 * Re-exchange when the token is near death, so a long call (or a reconnect) never fails mid-step.
 * Cheap and idempotent — the server mints freely for a live session.
 */
export async function refreshSessionTokenIfNeeded(sessionId: string): Promise<void> {
  const nearDeath = tokenExpiresAtMs > 0 && Date.now() > tokenExpiresAtMs - 5 * 60 * 1000;
  if (nearDeath) {
    await exchangeSessionToken(sessionId);
  }
}

/**
 * Read the in-memory session JWT (for the one non-fetch transport: the XHR
 * document upload, which needs real byte-level progress events). Memory-only —
 * the token never touches storage, URLs, or logs (same posture as `request`).
 */
export function getSessionToken(): string | null {
  return sessionToken;
}

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /**
     * The parsed JSON error body when the server sent one (RFC 9457
     * ProblemDetail on the vCIP_be seams — may carry machine-readable extras
     * like `errorCode` and a refreshed booking `grid`). Callers must treat it
     * as untrusted input and pick out only the fields they need.
     */
    public body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'DELETE';
  /** JSON-serialisable body — or a FormData for multipart uploads (sent as-is). */
  body?: unknown;
  signal?: AbortSignal;
}

export async function request<T>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const url = `${API_BASE_URL}${path}`;
  // FormData is passed through untouched (the browser sets the multipart
  // boundary Content-Type itself — setting it manually would break the upload).
  const isForm = typeof FormData !== 'undefined' && opts.body instanceof FormData;
  // 18-Aug RCA (token-exchange race): the routes fire `exchangeSessionToken()` and fetch on the
  // very next line, so the first authed reads (GET /sessions/{id}, GET /checks) left the door
  // BEFORE the bearer existed and 401'd — 12×401 vs 2×200 on the plan read across 16–18 Aug.
  // Generic belt: if an exchange is in flight, every request waits for it before building headers.
  if (exchangeInFlight) {
    try {
      await exchangeInFlight;
    } catch {
      /* the exchange never rejects (see above); belt only */
    }
  }
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (opts.body !== undefined && !isForm) headers['Content-Type'] = 'application/json';
  if (sessionToken) headers['Authorization'] = `Bearer ${sessionToken}`;

  const init: RequestInit = { method: opts.method ?? 'GET', headers };
  if (opts.body !== undefined) {
    init.body = isForm ? (opts.body as FormData) : JSON.stringify(opts.body);
  }
  if (opts.signal) init.signal = opts.signal;

  const res = await fetch(url, init);
  if (!res.ok) {
    // Surface the server's own message when it sent one (the artefact/queue
    // contract names what's wrong in 409/422 bodies, e.g. CONSENT_REQUIRED) —
    // callers branch on ApiError.status + message content, never a bare code.
    //
    // The PARSED body rides along too. `ApiError.body` was documented as
    // carrying the machine-readable extras (`errorCode`, a refreshed booking
    // grid) since the class was written, but nothing ever populated it — so a
    // typed problem such as ARRIVAL_BEFORE_WINDOW reached callers as prose they
    // would have had to string-match. The body is read ONCE here (a Response
    // stream can only be consumed once) and handed over untrusted: callers pick
    // out only the fields they need, and a caller that wants none is unaffected.
    const problem = await readProblem(res, `${init.method ?? 'GET'} ${path} → ${res.status}`);
    throw new ApiError(res.status, problem.message, problem.body);
  }
  return (await res.json()) as T;
}

/** What an error response yielded: the sentence to show, and the parsed body (null when unusable). */
interface ParsedProblem {
  message: string;
  body: unknown;
}

/**
 * Best-effort parse of an error body: RFC7807 `{detail}` / `{title}`, then `{message}` / `{error}`,
 * else a plain-text line. NEVER the raw JSON body.
 *
 * <p><strong>The defect this fixes (owner, 2026-08-25).</strong> The be answers with problem+json —
 * `{type,title,status,detail}` — and this parser looked only for `message`/`error`. Neither matched,
 * so it fell through to `text.slice(0, 300)` and the CUSTOMER was shown the raw body on their phone:
 * `{"type":"about:blank","title":"Capture requirement not met","status":422,"detail":"capture window
 * closed for session 01a03732-… (status=LIVE; artefacts are accepted only at INITIATED …"`. A
 * server diagnostic, a session UUID and an internal state machine, rendered as instructions to a
 * member of the public.
 *
 * <p>`detail` is read first because that is where the be puts the sentence a human should read; a
 * JSON-shaped body is never returned verbatim now, even when no field matches — an unparseable
 * error means the caller's own copy is the honest thing to show, not the wire.
 */
async function readProblem(res: Response, fallback: string): Promise<ParsedProblem> {
  try {
    const text = await res.text();
    if (!text) return { message: fallback, body: null };
    try {
      const parsed = JSON.parse(text) as {
        detail?: unknown; title?: unknown; message?: unknown; error?: unknown;
      };
      // JSON we cannot read a sentence out of still yields its BODY — the
      // machine-readable half is useful even when the prose half is not.
      const message =
        (typeof parsed.detail === 'string' && parsed.detail) ||
        (typeof parsed.message === 'string' && parsed.message) ||
        (typeof parsed.error === 'string' && parsed.error) ||
        (typeof parsed.title === 'string' && parsed.title) ||
        fallback;                            // never dump the body at a customer
      return { message, body: parsed };
    } catch {
      /* not JSON */
    }
    const plain = text.trim();
    // A JSON-looking body that failed to parse is still a body, not a sentence.
    if (plain.startsWith('{') || plain.startsWith('[')) return { message: fallback, body: null };
    return { message: plain.slice(0, 300), body: null };
  } catch {
    return { message: fallback, body: null };
  }
}

/** Small helper for mock timing so fixtures feel like a network round-trip. */
export function delay(ms: number): Promise<void> {
  return new Promise((r) => setTimeout(r, ms));
}
